import { useCallback, useEffect, useRef, useState } from 'react'
import { stopStream } from './utils'

/**
 * Microphone-only recorder used by the clip editor to record a voiceover
 * narration track that can replace or be mixed with a clip's original audio.
 */
const useAudioRecorder = ({ onClipReady, onError } = {}) => {
  const [status, setStatus] = useState('idle') // idle | requesting | recording
  const [elapsedSec, setElapsedSec] = useState(0)

  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const streamRef = useRef(null)
  const timerRef = useRef(null)
  const startedAtRef = useRef(0)

  const cleanup = useCallback(() => {
    stopStream(streamRef.current)
    streamRef.current = null
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }, [])

  const finalize = useCallback(() => {
    const mimeType = recorderRef.current?.mimeType || 'audio/webm'
    const blob = new Blob(chunksRef.current, { type: mimeType })
    chunksRef.current = []
    cleanup()
    setStatus('idle')
    setElapsedSec(0)
    if (blob.size > 0) onClipReady && onClipReady(blob)
  }, [cleanup, onClipReady])

  const start = useCallback(async () => {
    try {
      setStatus('requesting')
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
      streamRef.current = stream
      chunksRef.current = []
      const recorder = new MediaRecorder(stream)
      recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data) }
      recorder.onstop = finalize
      recorder.onerror = (e) => { onError && onError(e.error || new Error('Voiceover recording failed')); finalize() }
      recorderRef.current = recorder
      recorder.start(1000)
      startedAtRef.current = Date.now()
      setStatus('recording')
      timerRef.current = setInterval(() => setElapsedSec((Date.now() - startedAtRef.current) / 1000), 250)
    } catch (err) {
      cleanup()
      setStatus('idle')
      onError && onError(err)
    }
  }, [cleanup, finalize, onError])

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop()
  }, [])

  useEffect(() => () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch { /* noop */ }
    }
    cleanup()
  }, [cleanup])

  return { status, elapsedSec, start, stop }
}

export default useAudioRecorder
